"use client";

// Login screen (C3) — rewritten from reference/scopely-frontend/src/app/
// login/page.tsx for the dual-brand ZW/VIP shell (B1 tokens) and the
// measured PD-1 contract (C1 authApi + C2 session). Layout and field
// validation UX carry over; error rendering is driven by the measured
// login fixtures:
//   - bad-credentials.json (401)             -> AuthApiError.detail in the
//     top alert banner.
//   - empty-body-validation.json (400)       -> AuthApiError.fieldErrors
//     rendered per field, no duplicate top banner.
//
// Divergences from the reference (full list in the module README):
//   - No Google sign-in: the reference gates it behind an unset env flag by
//     default and no OAuth client id is configured here either — dead code
//     either way, so it is omitted rather than ported disabled.
//   - S7: under the SERVED vip brand this page renders the reference's
//     full chrome — the PSCX VIP lockup + Revenue-as-a-Service marketing
//     panel, ThemeToggle, and the Request Access footer link. Under zw it
//     keeps the plain card (the C3 layout). The chrome branches on
//     useCurrentBrand — and since 2026-08-29 so does ONE field: the
//     identifier input. ZW posts a username, VIP posts an email, and
//     type="email" blocked the former in the browser before it could be
//     submitted. That is the first screen every ZW user sees at cutover, so
//     the form is no longer a single implementation and this note says so
//     rather than leaving the older claim standing as a lie.
//   - The forced-reset step's SUBMIT calls authApi.changePassword (the
//     nearest measured case: POST /change-password/, current_password +
//     new_password) instead of the reference's dedicated
//     force-change-password/ endpoint. That endpoint has no fixture in this
//     corpus — FIXTURE GAP, flagged in the lane report and the module
//     README. The reveal UI itself (new/confirm fields, 12-char minimum,
//     identity-resemblance check) is real measured behavior: it is gated on
//     AuthUser.must_reset_password, a field in the login/success fixture's
//     user envelope.
import { Suspense, useEffect, useState, type FormEvent } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowRight } from "lucide-react";

import {
  authApi,
  AuthApiError,
  MIN_PASSWORD_LENGTH,
  PasswordRequirements,
  useAuth,
  type AuthUser,
} from "@/modules/auth";
import { resolvePostLoginRoute } from "@/shared/navigation";
import { Button, Input, PasswordInput, ThemeToggle, useCurrentBrand } from "@/shared/ui";

/** The reference login's brand lockup (its PscxVipLockup, ported intact). */
function PscxVipLockup({
  className = "h-12",
  align = "center",
}: {
  className?: string;
  align?: "center" | "start";
}) {
  return (
    <div className={`flex items-center ${align === "start" ? "justify-start" : "justify-center"}`}>
      {/* eslint-disable-next-line @next/next/no-img-element -- static brand asset, same idiom as the reference */}
      <img src="/pscx-vip-logo.svg" alt="PSCX VIP" className={`${className} w-auto`} />
    </div>
  );
}

function LoginPageInner() {
  const router = useRouter();
  const { login, loginWithUser, user, isPartnerUser } = useAuth();
  // Where middleware wanted this user to end up before it bounced them
  // here. Re-validated rather than trusted: ?next= is user-controlled.
  const rawNext = useSearchParams().get("next");
  // Resolved against the PERSONA, not just the URL (#519): a partner holds
  // no org membership, so the default /dashboard landing renders an error
  // card for them. `isPartnerUser` is null-safe before the session resolves
  // (it reads `user?.partner`), and the redirect below only fires once
  // `user` is set — so by the time this value is used it is the real one.
  const nextRoute = resolvePostLoginRoute(rawNext, { isPartnerUser });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // ZW's consent step (contract v2.24). Legacy ZW requires terms at every
  // login and remembers the tick in localStorage under this exact key, so a
  // returning user is not asked twice — same key, so a user who accepted on
  // the legacy app is not re-prompted after cutover.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);
  const [pendingResetUser, setPendingResetUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const isVip = useCurrentBrand() === "vip";

  useEffect(() => {
    if (user && !user.must_reset_password) {
      router.push(nextRoute);
    }
  }, [user, router, nextRoute]);

  // MOUNT ONLY — deliberately not keyed on `isVip`. useCurrentBrand resolves
  // the brand in its own mount effect, so an [isVip] dependency re-runs this
  // the moment the brand settles and OVERWRITES a tick the user has already
  // made. Caught by the login tests: the box was checked, the brand resolved,
  // the tick was silently cleared, and the form then refused to submit with
  // no visible reason. Reading storage under VIP is harmless — the value is
  // unused there. Must sit ABOVE the early return below: a hook after a
  // conditional return changes the hook order the render a session resolves.
  useEffect(() => {
    try {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydration-safe seed from localStorage on mount
      setTermsAccepted(
        window.localStorage.getItem("zw_terms_accepted") === "true",
      );
    } catch {
      /* storage-less contexts simply ask again — never a silent accept */
    }
  }, []);

  // Session already resolved to a signed-in, non-reset-pending user (e.g. a
  // direct visit while a cookie session is live) — the redirect above is in
  // flight, render nothing rather than flash the form.
  if (user && !user.must_reset_password) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors(null);
    try {
      if (requiresPasswordReset) {
        if (newPassword !== confirmPassword) {
          setError("New passwords do not match");
          setLoading(false);
          return;
        }
        await authApi.changePassword(password, newPassword);
        const me = await authApi.me();
        loginWithUser(me);
        // Read the persona off the freshly-fetched user rather than off
        // `isPartnerUser`: loginWithUser has not re-rendered this component
        // yet, so the context flag is still the signed-out `false` here.
        router.push(
          resolvePostLoginRoute(rawNext, { isPartnerUser: me.partner != null }),
        );
      } else {
        // undefined for VIP: their request stays byte-identical to the
        // measured shape rather than carrying a field their form never had.
        const result = await login(
          email,
          password,
          isVip ? undefined : termsAccepted,
        );
        if (!isVip && termsAccepted) {
          try {
            window.localStorage.setItem("zw_terms_accepted", "true");
          } catch {
            /* remembering is a convenience; the server holds the record */
          }
        }
        if (result.user.must_reset_password) {
          setRequiresPasswordReset(true);
          setPendingResetUser(result.user);
        }
      }
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.fieldErrors) {
          // Field-level messages already say everything; skip the redundant
          // top banner (the fixture's `detail` is a concatenated summary
          // string, not something worth repeating per field).
          setFieldErrors(err.fieldErrors);
        } else {
          setError(err.detail ?? "Login failed. Please try again.");
        }
      } else {
        setError(err instanceof Error ? err.message : "Login failed. Please try again.");
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  }

  const card = (
    <div className="ds-card rounded-3xl border border-edge p-8 shadow-sm">
      {isVip ? (
        <div className="lg:hidden mb-8">
          <PscxVipLockup />
        </div>
      ) : (
        <h1 className="mb-6 text-2xl font-black tracking-tight text-ink">Sign in</h1>
      )}

      {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {requiresPasswordReset && (
            <div
              role="status"
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              Your password was reset by an administrator. Choose a new password to continue.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="mb-1 block text-sm font-medium text-ink">
                {isVip ? "Email" : "Username or email"}
              </label>
              <Input
                id="login-email"
                // ZW users sign in with a USERNAME (legacy LoginForm posts
                // one). `type="email"` is a BROWSER-LEVEL block: a username
                // cannot even be submitted for validation, so every ZW user
                // whose username is not their address met a door that does
                // not open. VIP keeps type="email" and its format hint —
                // widening it for them would be a small regression they have
                // no reason to absorb. Backend accepts either (contract
                // v2.23) and refuses ambiguity.
                type={isVip ? "email" : "text"}
                autoComplete={isVip ? "email" : "username"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                error={Boolean(fieldErrors?.email)}
                aria-describedby={fieldErrors?.email ? "login-email-error" : undefined}
              />
              {fieldErrors?.email && (
                <p id="login-email-error" className="mt-1 text-xs text-red-600">
                  {fieldErrors.email.join(" ")}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1 block text-sm font-medium text-ink">
                Password
              </label>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                aria-invalid={Boolean(fieldErrors?.password) || undefined}
                aria-describedby={fieldErrors?.password ? "login-password-error" : undefined}
                className={`h-10 w-full rounded-lg border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint ${
                  fieldErrors?.password ? "border-red-500" : "border-edge"
                }`}
              />
              {fieldErrors?.password && (
                <p id="login-password-error" className="mt-1 text-xs text-red-600">
                  {fieldErrors.password.join(" ")}
                </p>
              )}
            </div>

            {/* ZW's consent step (v2.24). VIP has no such step and must not
                grow one — the checkbox renders for ZW only, and it is
                `required` there exactly as the legacy form has it, so a login
                cannot proceed without it. Hidden during the forced-reset
                reveal: that step is not a login and asking again would record
                an acceptance the user did not make in that flow. */}
            {!isVip && !requiresPasswordReset && (
              <div className="flex items-start gap-2">
                <input
                  id="terms_accepted"
                  name="terms_accepted"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 rounded border-edge"
                />
                <label htmlFor="terms_accepted" className="text-sm text-ink">
                  I agree to the Terms &amp; Conditions
                </label>
              </div>
            )}

            {requiresPasswordReset && (
              <>
                <div>
                  <label htmlFor="login-new-password" className="mb-1 block text-sm font-medium text-ink">
                    New Password
                  </label>
                  <PasswordInput
                    id="login-new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    className="h-10 w-full rounded-lg border border-edge bg-surface px-3 text-sm text-ink placeholder:text-ink-faint"
                  />
                  <PasswordRequirements
                    password={newPassword}
                    email={pendingResetUser?.email ?? email}
                    username={pendingResetUser?.username}
                    firstName={pendingResetUser?.first_name}
                    lastName={pendingResetUser?.last_name}
                    className="mt-2 space-y-1"
                  />
                </div>
                <div>
                  <label htmlFor="login-confirm-password" className="mb-1 block text-sm font-medium text-ink">
                    Confirm Password
                  </label>
                  <PasswordInput
                    id="login-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    className="h-10 w-full rounded-lg border border-edge bg-surface px-3 text-sm text-ink placeholder:text-ink-faint"
                  />
                </div>
              </>
            )}

            <Button type="submit" loading={loading} className="w-full">
              {loading
                ? requiresPasswordReset
                  ? "Updating Password..."
                  : "Signing in..."
                : requiresPasswordReset
                  ? "Change Password and Continue"
                  : "Sign In"}
            </Button>
          </form>

          {!requiresPasswordReset && (
            <div className="mt-3 text-right">
              <Link href="/forgot-password" className="text-sm text-accent hover:underline">
                Forgot password?
              </Link>
            </div>
          )}

      {isVip && !requiresPasswordReset && (
        <p className="mt-5 text-center text-sm text-ink-light">
          Need access?{" "}
          <Link
            href="/register"
            className="inline-flex items-center gap-1 text-accent hover:underline font-medium"
          >
            Request Access
            <ArrowRight size={13} />
          </Link>
        </p>
      )}
    </div>
  );

  if (isVip) {
    // The reference login's full chrome: marketing panel + lockup on
    // desktop, theme toggle floated top-right, card right-aligned.
    return (
      <div className="min-h-screen bg-page relative overflow-hidden px-6 py-10">
        <div className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6">
          <ThemeToggle />
        </div>

        <main className="relative z-10 mx-auto grid min-h-[calc(100vh-5rem)] w-full max-w-6xl items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="hidden lg:flex flex-col gap-8">
            <PscxVipLockup align="start" className="h-24" />
            <div>
              <p className="text-sm font-black uppercase tracking-[0.32em] text-accent">
                Revenue-as-a-Service
              </p>
              <h1 className="mt-5 max-w-2xl text-5xl font-black tracking-tight text-ink">
                Voice Intelligence Platform
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-ink-light">
                Create SOWs, manage migrations, and capture services revenue in one dashboard
              </p>
            </div>
          </section>

          <section className="mx-auto w-full max-w-md">{card}</section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page px-6 py-10">
      <main className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-md flex-col justify-center">
        {card}
      </main>
    </div>
  );
}

/**
 * useSearchParams() opts a route into client-side rendering, and Next requires
 * the boundary to be explicit -- without this wrapper `next build` fails the
 * prerender rather than warning. fallback={null} because the form is already
 * the first paint on a bare /login visit and a skeleton would only flash.
 */
export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageInner />
    </Suspense>
  );
}
